import { makeAutoObservable, runInAction } from "mobx"
import type { TaskAttention } from "../persistence/records"

export function taskIsSleeping(task: { closed?: boolean; attention?: TaskAttention }, now = Date.now()): boolean {
    const until = task.attention?.sleepUntil
    return !task.closed && !!until && (until === "indefinite" || Date.parse(until) > now)
}

export function taskAttentionAt(attention: TaskAttention | undefined, now = Date.now()): string | undefined {
    if (!attention) return undefined
    if (!attention.sleepUntil) return attention.at
    if (attention.sleepUntil !== "indefinite" && Date.parse(attention.sleepUntil) <= now) return attention.sleepUntil
    return undefined
}

export function taskSleepLabel(attention: TaskAttention | undefined): string {
    if (attention?.source === "pr" && attention.sleepUntil === "indefinite") return "Watching PR"
    if (!attention?.sleepUntil || attention.sleepUntil === "indefinite") return "Sleeping"
    return `Sleeping until ${new Date(attention.sleepUntil).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}`
}

// One presentation clock over active deadlines, updated by individual record changes.
// No provider work, persistence writes, or history scans happen when a deadline expires.
export class TaskAttentionClock {
    now = Date.now()
    private deadlines = new Map<string, number>()
    private timer: ReturnType<typeof setTimeout> | null = null
    private nextAt: number | null = null

    constructor() {
        makeAutoObservable<TaskAttentionClock, "deadlines" | "timer" | "nextAt">(this, { deadlines: false, timer: false, nextAt: false })
    }

    observe(taskId: string, task: { closed?: boolean; attention?: TaskAttention } | undefined): void {
        const until = task?.attention?.sleepUntil
        const at = until && until !== "indefinite" && !task?.closed ? Date.parse(until) : NaN
        const deadline = Number.isFinite(at) && at > Date.now() ? at : undefined
        if (this.deadlines.get(taskId) === deadline && (deadline !== undefined || !Number.isFinite(at))) return
        const previous = this.deadlines.get(taskId)
        this.now = Date.now()
        if (deadline !== undefined) this.deadlines.set(taskId, deadline)
        else this.deadlines.delete(taskId)
        if (this.nextAt !== null && previous !== this.nextAt && (deadline === undefined || deadline >= this.nextAt)) return
        this.schedule()
    }

    dispose(): void {
        if (this.timer !== null) clearTimeout(this.timer)
        this.timer = null
        this.nextAt = null
        this.deadlines.clear()
    }

    private schedule(): void {
        let next: number | null = null
        for (const deadline of this.deadlines.values()) if (next === null || deadline < next) next = deadline
        if (next === this.nextAt) return
        if (this.timer !== null) clearTimeout(this.timer)
        this.nextAt = next
        this.timer =
            next === null
                ? null
                : setTimeout(
                      () => {
                          runInAction(() => {
                              const now = Date.now()
                              if (this.nextAt !== null && now >= this.nextAt) {
                                  this.now = now
                                  for (const [id, deadline] of this.deadlines) if (deadline <= now) this.deadlines.delete(id)
                              }
                              this.nextAt = null
                              this.timer = null
                              this.schedule()
                          })
                      },
                      Math.min(60_000, Math.max(0, next - Date.now()))
                  )
    }
}
